import * as React from 'react';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import type { EpaQuestions } from './models/JobInterfaces'
import type { GenericList } from './models/JobSharedInterfaces';
import JobQuoteEpaQuestions from './JobQuoteEpaQuestions';
import { GetQuery, PostQuery } from './Shared';

interface JobQuoteEpaQuestionsModalProps {
    showModal: boolean;
    isReadOnly: boolean;
    jobId: number;
    jobQuoteItemId: number;
    itemTypeId: number;
    itemId: number;
    item: string;
    hideModal(): void;
    saveModal(tankLidExposed: boolean, confinedSpace: boolean, weekendJob: boolean, emergencySameDayJob: boolean, updatedInvoiceDescription: string, updatedIncludeGreaseTrap: boolean): void;
}

const JobQuoteEpaQuestionsModalData = (props: JobQuoteEpaQuestionsModalProps) => {
    const [epaQuestions, setEpaQuestions] = React.useState<EpaQuestions>({
        tankLidExposed: -1,
        tankLidLocationKnown: -1,
        tankLidLocation: "",
        truckLargeAccess: -1,
        truckSmallAccess: -1,
        truckAccessToTank: -1,  //within 25m
        steepDriveway: -1,
        hoseMetres: 0,
        sealedDriveway: -1,
        lastService: "",
        waterBoard: "",
        waterAccessOnSite: -1,
        emergencySameDayJob: -1,
        weekendJob: -1,
        siteVisit: -1,
        confinedSpace: -1,
        materialSafetyDataSheet: -1,
        sampleTestingRequired: -1
    });
    const [yesNoOptions, setYesNoOptions] = React.useState<GenericList[]>([]);
    const [epaLidLocationFeeFormatted, setEpaLidLocationFeeFormatted] = React.useState("$0.00");
    const [weekendFeeFormatted, setWeekendFeeFormatted] = React.useState("$0.00");
    const [emergencyFeeFormatted, setEmergencyFeeFormatted] = React.useState("$0.00");
    const [invoiceDescription, setInvoiceDescription] = React.useState("");
    const [includeGreaseTrap, setIncludeGreaseTrap] = React.useState(false);
    const [saveDisabled, setSaveDisabled] = React.useState(false);

    React.useEffect(() => {
        if (props.jobQuoteItemId > 0) {
            getData();
        }
    }, [props.jobQuoteItemId]);

    const getData = async () => {
        const apiUrl = 'GetEpaQuestions?JobQuoteItemId=' + props.jobQuoteItemId;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setEpaQuestions(result.data.epaQuestions);
            setYesNoOptions(result.data.yesNoOptions);
            setEpaLidLocationFeeFormatted(result.data.epaExtra30MinOnSiteFeeFormatted);
            setWeekendFeeFormatted(result.data.weekendFeeFormatted);
            setEmergencyFeeFormatted(result.data.emergencyFeeFormatted);
            setInvoiceDescription(result.data.invoiceDescription);
            setIncludeGreaseTrap(result.data.includeGreaseTrap);
            setSaveDisabled(false);
        }
    }

    const handleEpaQuestionChange = (fieldName:string, fieldValue: number) => {
        setEpaQuestions(prevState => ({ ...prevState, [fieldName]: fieldValue }));

        if (fieldName === "truckAccessToTank" || fieldName === "steepDriveway") {
            setEpaQuestions(prevState => ({ ...prevState, hoseMetres: 0 }));
        } else if (fieldName === "tankLidLocationKnown") {
            setEpaQuestions(prevState => ({ ...prevState, tankLidLocation: "" }));
        } else if (fieldName === "weekendJob") {
            setEpaQuestions(prevState => ({ ...prevState, emergencySameDayJob: -1 }));
        } else if (fieldName === "tankLidExposed") {
            setEpaQuestions(prevState => ({ ...prevState, tankLidLocationKnown: -1, tankLidLocation: "" }));
        }
    }

    const handleEpaQuestionTextChange = (fieldName: string, fieldValue: string) => {
        setEpaQuestions(prevState => ({ ...prevState, [fieldName]: fieldValue }));
    }

    const handleInvoiceDescriptionChange = (updatedInvoiceDescription: string) => {
        setInvoiceDescription(updatedInvoiceDescription);
    }

    const handleIncludeGreaseTrapChange = (updatedIncGreaseTrap: boolean) => {
        setIncludeGreaseTrap(updatedIncGreaseTrap);
    }

    const updateSaveDisabled = (saveDisUpdated: boolean) => {
        setSaveDisabled(saveDisUpdated);
    }

    const saveEpa = async () => {
        var saveData = {
            jobId: props.jobId,
            jobQuoteItemId: props.jobQuoteItemId,
            itemId: props.itemId,
            epaQuestions: epaQuestions,
            invoiceDescription: invoiceDescription,
            trolley: includeGreaseTrap
        };

        var jsonItem = JSON.stringify(saveData);

        const apiUrl = "SaveEpaQuestions";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
                setSaveDisabled(false);
            } else {
                setSaveDisabled(false);
                toast.success("Questions Saved");
                props.saveModal(epaQuestions.tankLidExposed == 1, epaQuestions.confinedSpace == 1, epaQuestions.weekendJob == 1, epaQuestions.emergencySameDayJob == 1, invoiceDescription, includeGreaseTrap);
            }
        }
    }

    return (
        <Modal size='lg' show={props.showModal} onHide={props.hideModal}>
            <Modal.Header className="modal__header--colour">
                <Modal.Title>EPA Questions</Modal.Title>
                <button type="button" className="close" onClick={props.hideModal}>&times;</button>
            </Modal.Header>
            <Modal.Body>
                <span className="makeitred">Note: Updating the questions may re-calcuate the price</span>
                <JobQuoteEpaQuestions isReadOnly={props.isReadOnly} showInvoiceDescription={true} itemTypeId={props.itemTypeId} epaLidLocationFeeFormatted={epaLidLocationFeeFormatted} weekendFeeFormatted={weekendFeeFormatted} emergencyFeeFormatted={emergencyFeeFormatted} epaQuestions={epaQuestions} invoiceDescription={invoiceDescription} includeGreaseTrap={includeGreaseTrap} yesNoOptions={yesNoOptions} showBack={false} saveDisabled={saveDisabled} updateNumber={handleEpaQuestionChange} updateText={handleEpaQuestionTextChange} updateInvoiceDescription={handleInvoiceDescriptionChange} updateIncGreaseTrap={handleIncludeGreaseTrapChange} updateSaveDisabled={updateSaveDisabled} save={saveEpa} />
            </Modal.Body>
        </Modal>
    )
}

export default JobQuoteEpaQuestionsModalData;

